import styled, { css } from 'styled-components';
import LinkComponent from '@components/link';
import { generateScales } from '@helpers/generate-media-queries';
import Image from '@components/image';
import Text from '@components/text';
import Section from '@components/section';
import { defaultTheme } from '@styles/theme.styled';

export const StyledItemText = styled(Text)`
  ${({ theme }) => css`
    font-size: 1rem;

    @media (max-width: ${theme.breakpoints.sm}) {
      font-size: 0.9375rem;
    }
  `}
`;

export const StyledSection = styled(Section)<{ hasEmbed?: boolean }>`
  ${({ hasEmbed }) => css`
    ${hasEmbed &&
    css`
      // padding-top: 0;
    `}
  `}
`;

interface FeatureWrapperProps {
  $imageAlignment?: 'default' | 'left' | 'right';
  hasEmbed?: boolean;
  hasImage?: boolean;
}

type ImageAlignmentOption = 'default' | 'left' | 'right';

const alignmentDirectionStyle = (imageAlignment?: ImageAlignmentOption) => {
  switch (imageAlignment) {
    case 'default':
      return css`
        flex-direction: row;
      `;
    case 'left':
      return css`
        flex-direction: row-reverse;
      `;
    case 'right':
      return css`
        flex-direction: row;
      `;
    default:
      return css``;
  }
};

export const FeatureWrapper = styled.div<FeatureWrapperProps>`
  ${({ theme, hasEmbed, $imageAlignment, hasImage }) => css`
    display: flex;
    flex-direction: ${hasEmbed || $imageAlignment === 'left' ? 'row' : 'row-reverse'};
    align-items: ${hasEmbed ? 'flex-start' : 'flex-start'};
    justify-content: center;

    margin: ${hasImage || hasEmbed ? '0' : '0 auto'};
    gap: 4rem;
    max-width: ${hasImage || hasEmbed ? '100%' : '54rem'};

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      gap: 3rem;
      align-items: stretch;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      gap: 2rem;
      flex-direction: ${hasEmbed ? 'column' : alignmentDirectionStyle($imageAlignment)};
      padding: 0;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      gap: 2rem;
      flex-direction: column;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      padding: 0;
      gap: 2rem;
    }
  `}
`;

export const TitleContainer = styled.div<{ hasEmbed?: boolean; hasImage?: boolean }>`
  ${({ theme, hasEmbed, hasImage }) => css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-width: ${hasEmbed || hasImage ? '100%' : '54rem'};
    text-align: ${hasEmbed || hasImage ? 'left' : 'center'};

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      max-width: ${hasEmbed || hasImage ? '100%' : '36rem'};
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      gap: 1rem;
      text-align: left;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      gap: 0.5rem;
    }
  `}
`;

interface FeatureListWrapperProps {
  itemCount?: number;
}

export const FeatureListWrapper = styled.div<FeatureListWrapperProps>`
  ${({ theme, itemCount }) => css`
    display: grid;
    grid-template-columns: repeat(${itemCount && itemCount < 4 ? 1 : 2}, 1fr);
    width: 100%;
    row-gap: 1.5rem;
    column-gap: 3rem;
    margin-top: 1rem;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      margin-top: 0.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      grid-template-columns: repeat(1, 1fr);
      row-gap: 1.5rem;
      column-gap: 1.5rem;
    }
  `}
`;

export const FeatureItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  align-items: flex-start;
  justify-content: flex-start;
`;

export const IconWrapper = styled.div`
  width: 100%;
  max-width: 1.25rem;
  height: 1.25rem;
  margin-top: 0.25rem;
`;

export const FeatureListIcon = styled(Image)`
  width: 100%;
  height: 100%;
`;

export const FeatureListDescription = styled.div`
  width: 100%;
`;

interface TextContentProps {
  hasItems?: boolean;
  hasEmbed?: boolean;
  hasImage?: boolean;
}

export const TextContent = styled.div<TextContentProps>`
  ${({ theme, hasEmbed, hasImage }) => css`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
    align-items: ${hasEmbed || hasImage ? 'flex-start' : 'center'};

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      padding: 0;
      gap: 1.25rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      row-gap: 1rem;
      width: 100%;
      padding: 0;
      align-items: flex-start;
    }
  `}
`;

type ImageCropOption = 'none' | 'top' | 'bottom' | 'left' | 'right';

const cropPaddingStyles = (imageCrop?: ImageCropOption) => {
  switch (imageCrop) {
    case 'top':
      return css`
        padding-top: 0;
      `;
    case 'bottom':
      return css`
        padding-bottom: 0;
      `;
    case 'left':
      return css`
        padding-bottom: 0;
        padding-left: 0;
      `;
    case 'right':
      return css`
        padding-bottom: 0;
        padding-right: 0;
      `;
    default:
      return css``;
  }
};

const cropBorderRadiusStyles = (defaultRadius: string, imageCrop?: ImageCropOption) => {
  switch (imageCrop) {
    case 'top':
      return css`
        border-radius: ${defaultRadius};
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      `;
    case 'bottom':
      return css`
        border-radius: ${defaultRadius};
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      `;
    case 'left':
      return css`
        border-radius: 0;
        border-top-right-radius: ${defaultRadius};
      `;
    case 'right':
      return css`
        border-radius: 0;
        border-top-left-radius: ${defaultRadius};
      `;
    default:
      return css`
        border-radius: ${defaultRadius};
      `;
  }
};

interface ImageContainerProps {
  imageFit?: boolean;
  imageCrop?: ImageCropOption;
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
}

export const ImageContainer = styled.div<ImageContainerProps>`
  ${({ theme, imageFit, imageCrop, backgroundColor }) => css`
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: flex-start;
    border-radius: ${theme.borderRadius.large};
    overflow: hidden;
    background-color: ${(() => {
      if (imageFit) {
        return 'transparent';
      }

      return backgroundColor === 'white'
        ? theme.backgroundColors.light
        : theme.backgroundColors.white;
    })()};
    padding: 0;

    ${!imageFit &&
    css`
      padding: 3rem;
      ${cropPaddingStyles(imageCrop)}

      @media only screen and (max-width: ${theme.breakpoints.xl}) {
        padding: 2.5rem;
        ${cropPaddingStyles(imageCrop)}
      }

      @media only screen and (max-width: ${theme.breakpoints.lg}) {
        padding: 2rem;
        ${cropPaddingStyles(imageCrop)}
        border-radius: 1.5rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        padding: 2rem;
        ${cropPaddingStyles(imageCrop)}
      }

      @media only screen and (max-width: ${theme.breakpoints.sm}) {
        padding: 1rem;
        ${cropPaddingStyles(imageCrop)}
        border-radius: ${theme.borderRadius.large};
      }
    `}

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      height: unset;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      justify-content: center;
      width: 100%;
    }
  `}
`;

export const StyledText = styled(Text)`
  ${({ theme }) => css`
    ul {
      font-size: 1rem;
      line-height: 1.3;
      column-count: 2;
      column-gap: 2rem;

      @media only screen and (max-width: ${theme.breakpoints.xl}) {
        column-count: 2;
      }

      @media only screen and (max-width: ${theme.breakpoints.lg}) {
        column-count: 2;
        column-gap: 1rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        column-count: 1;
        margin-top: 1.5rem;
        margin-left: 1.5rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.sm}) {
        column-count: 1;
      }

      li + li {
        margin-top: 1rem;

        @media only screen and (max-width: ${theme.breakpoints.md}) {
          margin-top: 0.75rem;
        }
      }
    }
  `}
`;

interface StyledImageProps {
  blendImage?: boolean;
  imageCrop?: ImageCropOption;
}

export const StyledImage = styled(Image)<StyledImageProps>`
  ${({ theme, blendImage, imageCrop }) => css`
    mix-blend-mode: ${blendImage ? 'multiply' : 'normal'};
    width: 100%;
    height: 100%;
    ${cropBorderRadiusStyles(theme.borderRadius.large, imageCrop)}

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      height: 100%;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      border-top-left-radius: ${theme.borderRadius.normal};
    }
  `}
`;

export const StyledLinkComponent = styled(LinkComponent)`
  ${({ theme }) => css`
    width: fit-content;
    margin: 1rem 0 0 0;
    border: 2px solid ${theme.colors.primary};
    background-color: transparent;
    color: ${theme.colors.secondary};
    ${generateScales('font-size', theme.fontSize.body.mobile, theme.fontSize.body.desktop, theme)}
    transition: all 0.125s ease-in-out;

    &:hover {
      transform: scale(1.0875);
      background-color: ${theme.colors.primary};
      border-color: ${theme.colors.primary};
      color: ${theme.colors.secondary};
    }

    &:active {
      transform: scale(0.9625);
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      margin: 0.5rem 0 0 0;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      margin: 0.25rem 0 0 0;
    }
  `}
`;

export const StyledItemLinkComponent = styled(LinkComponent)`
  ${({ theme }) => css`
    ${generateScales('font-size', theme.fontSize.small.mobile, theme.fontSize.small.desktop, theme)}
    margin: 0;
  `}
`;

export const EmbedContainer = styled.iframe`
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  border: 0;
`;

export const OverflowContainer = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;
    overflow: visible;
    position: relative;
    z-index: 1;
    background-color: ${theme.colors.light};
    padding: 0rem 0 4rem 4rem;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
      height: calc(100vh + 100%);
      width: 100vw;
      background-color: ${theme.colors.light};
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 100%;
      bottom: 0;
      width: 100vw;
      background-color: ${theme.colors.light};
    }

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      padding: 0rem 0 3rem 3rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      padding: 2rem 2rem 0 2rem;
      border-top-left-radius: 1.5rem;

      &::after {
        height: 100vh;
        transform: unset;
        top: 100%;
      }
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      padding: 2rem 0 0 2rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      padding: 1rem 0 0 1rem;

      border-top-left-radius: ${theme.borderRadius.large};
    }
  `}
`;
